import { createContext, useState, useContext, useEffect } from 'react';
import * as authAPI from '../utils/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  // Only show loading when token exists but no cached user (avoids blank screen on page load)
  const [loading, setLoading] = useState(
    !!localStorage.getItem('token') && !localStorage.getItem('user')
  );

  useEffect(() => {
    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUserProfile = async () => {
    // Background refresh — user already initialized from localStorage, so no loading state needed
    try {
      const profile = await authAPI.userAPI.getProfile();
      setUser(profile);
      localStorage.setItem('user', JSON.stringify(profile));
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Only logout on auth errors (401), not network errors
      if (error.message && (error.message.includes('401') || error.message.includes('Invalid token') || error.message.includes('No token'))) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, password, role = 'student') => {
    setLoading(true);
    try {
      const response = await authAPI.authAPI.signup({ name, email, password, role });
      
      if (response.error) {
        throw new Error(response.error);
      }

      // If server issued a token (e.g., admin signup), persist and set user.
      if (response.token && response.user) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setToken(response.token);
        setUser(response.user);
        return response;
      }

      // Account created but awaiting approval — return response for UI handling
      if (response.user) {
        return response;
      }

      throw new Error('Invalid response from server');
    } catch (error) {
      throw new Error(error.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, twoFactorCode) => {
    setLoading(true);
    try {
      const response = await authAPI.authAPI.login({ email, password, twoFactorCode });
      
      if (response.error) {
        throw new Error(response.error);
      }

      // 2FA step — backend says "enter your code"
      if (response.requires2FA) {
        return { requires2FA: true };
      }
      
      if (!response.token || !response.user) {
        throw new Error('Invalid response from server');
      }
      
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setToken(response.token);
      setUser(response.user);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, signup, login, logout, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
